import type { AppNotification, NotificationActionTarget, NotificationPriority } from '../types';
import { displayDate, formatMoney, monthKey, normalizeDateISO, todayISO } from './date.js';
import { buildFinancialHealthModel, type FinancialHealthIssue } from './financialHealth.js';
import type { ReportSourceData } from './reports';
import { buildBudgetUsage } from './budgets.js';

export type NotificationTab = 'all' | 'unread' | 'critical' | 'payments' | 'accounts' | 'other';
export type NotificationState = Pick<AppNotification, 'id' | 'status' | 'readAt' | 'readAtISO' | 'dismissedAt' | 'dismissedAtISO' | 'dedupeKey'> & Partial<AppNotification>;

const priorityRank: Record<Exclude<NotificationPriority, 'medium'>, number> = { critical: 0, high: 1, normal: 2, low: 3 };
const activePlan = (status?: string, active?: boolean) => active !== false && String(status || 'active').toLowerCase() === 'active';
const activeBudget = (status?: string, active?: boolean) => active !== false && String(status || 'active').toLowerCase() !== 'deleted';
const activeTransaction = (status?: string, active?: boolean) => active !== false && String(status || 'active').toLowerCase() !== 'deleted';
const cleanKey = (value: unknown) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'item';
const canonicalPriority = (priority?: NotificationPriority): Exclude<NotificationPriority, 'medium'> => priority === 'medium' ? 'normal' : priority || 'normal';
const dayNumber = (date: string) => {
  const [year, month, day] = normalizeDateISO(date).split('-').map(Number);
  return Math.floor(new Date(year, month - 1, day).getTime() / 86400000);
};
const daysBetween = (from: string, to: string) => dayNumber(to) - dayNumber(from);

function previousMonthKey(now: string) {
  const [year, month] = monthKey(now).split('-').map(Number);
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;
  return `${previousYear}-${String(previousMonth).padStart(2, '0')}`;
}

function hasMonthActivity(source: ReportSourceData, key: string) {
  return source.transactions.some((row) => row.monthKey === key && activeTransaction(row.status, row.active))
    || source.payments.some((row) => row.monthKey === key)
    || source.plans.some((row) => monthKey(row.nextDueDate || row.dueDate) === key && String(row.status || '').toLowerCase() !== 'deleted')
    || source.accountLedger.some((row) => row.monthKey === key);
}

function baseNotification(input: Omit<AppNotification, 'id' | 'uid' | 'status' | 'createdAtISO' | 'dedupeKey'> & {
  key: string;
  priority: Exclude<NotificationPriority, 'medium'>;
  status?: AppNotification['status'];
}): AppNotification {
  return {
    id: input.key,
    uid: '',
    dedupeKey: input.key,
    status: input.status || 'unread',
    dismissible: input.priority !== 'critical',
    createdAtISO: input.dateISO || input.dueDate || todayISO(),
    ...input
  };
}

function action(label: string, target: NotificationActionTarget) {
  return { label, target };
}

const healthCodesForAlerts = new Set([
  'BROKEN_TRANSFER_PAIR',
  'BROKEN_CARD_PAYMENT_PAIR',
  'MISSING_ACCOUNT_REFERENCE',
  'LEDGER_MISSING_ACCOUNT',
  'INVALID_TRANSACTION_AMOUNT'
]);

function healthIssueToNotification(issue: FinancialHealthIssue): AppNotification | undefined {
  if (issue.severity !== 'critical' && !healthCodesForAlerts.has(issue.code)) return undefined;
  if (!healthCodesForAlerts.has(issue.code)) return undefined;
  const key = `health:${issue.code}:${issue.entityId || issue.entityIds?.join('_') || issue.id}`;
  return baseNotification({
    key,
    type: 'health',
    category: 'health',
    source: 'financialHealth',
    priority: 'critical',
    title: issue.title,
    message: issue.description,
    relatedType: 'health',
    relatedId: issue.entityId || issue.id,
    entityId: issue.entityId,
    dateISO: issue.dateISO,
    action: action('Open Financial Health', 'health'),
    dismissible: false
  });
}

function storedEventNotification(row: NotificationState): AppNotification | undefined {
  if (!row.title || !row.message) return undefined;
  const priority = canonicalPriority(row.priority);
  const key = row.dedupeKey || row.id;
  return {
    id: key,
    uid: row.uid || '',
    dedupeKey: key,
    type: row.type || 'general',
    category: row.category || (row.type === 'plan_paid' ? 'payment' : 'system'),
    source: row.source || 'stored',
    title: row.title,
    message: row.message,
    relatedType: row.relatedType || 'system',
    relatedId: row.relatedId || key,
    entityId: row.entityId,
    dueDate: row.dueDate,
    dateISO: row.dateISO,
    status: row.status || 'unread',
    priority,
    action: row.action || (row.relatedType === 'plan' ? action('Open Plan', 'upcoming') : undefined),
    dismissible: row.dismissible ?? priority !== 'critical',
    createdAt: row.createdAt,
    createdAtISO: row.createdAtISO || row.dateISO || row.dueDate || todayISO(),
    readAt: row.readAt,
    readAtISO: row.readAtISO,
    dismissedAt: row.dismissedAt,
    dismissedAtISO: row.dismissedAtISO
  };
}

function mergeState(alerts: AppNotification[], states: NotificationState[]) {
  const stateByKey = new Map(states.map((state) => [state.dedupeKey || state.id, state]));
  return alerts.map((alert) => {
    const state = stateByKey.get(alert.dedupeKey || alert.id);
    if (!state) return alert;
    const status: AppNotification['status'] = state.status === 'dismissed' && alert.dismissible !== false && alert.priority !== 'critical' ? 'dismissed' : state.status === 'read' ? 'read' : 'unread';
    return {
      ...alert,
      status,
      readAt: state.readAt,
      readAtISO: state.readAtISO,
      dismissedAt: status === 'dismissed' ? state.dismissedAt : undefined,
      dismissedAtISO: status === 'dismissed' ? state.dismissedAtISO : undefined
    };
  });
}

function sortNotifications(rows: AppNotification[]) {
  return [...rows].sort((a, b) => {
    const rank = priorityRank[canonicalPriority(a.priority)] - priorityRank[canonicalPriority(b.priority)];
    if (rank) return rank;
    const aDate = a.dueDate || a.dateISO || a.createdAtISO || '';
    const bDate = b.dueDate || b.dateISO || b.createdAtISO || '';
    return aDate.localeCompare(bDate) || a.title.localeCompare(b.title) || (a.dedupeKey || a.id).localeCompare(b.dedupeKey || b.id);
  });
}

export function visibleNotifications(rows: AppNotification[]) {
  const byKey = new Map<string, AppNotification>();
  rows.forEach((row) => {
    const key = row.dedupeKey || row.id;
    const existing = byKey.get(key);
    if (!existing || priorityRank[canonicalPriority(row.priority)] < priorityRank[canonicalPriority(existing.priority)]) byKey.set(key, row);
  });
  return sortNotifications(Array.from(byKey.values()).filter((row) => row.status !== 'dismissed' || row.priority === 'critical' || row.dismissible === false));
}

export function filterNotifications(rows: AppNotification[], tab: NotificationTab) {
  if (tab === 'unread') return rows.filter((row) => row.status === 'unread');
  if (tab === 'critical') return rows.filter((row) => row.priority === 'critical');
  if (tab === 'payments') return rows.filter((row) => row.category === 'payment');
  if (tab === 'accounts') return rows.filter((row) => row.category === 'account' || row.category === 'credit_card' || row.category === 'reconciliation');
  if (tab === 'other') return rows.filter((row) => !['payment', 'account', 'credit_card', 'reconciliation'].includes(String(row.category)));
  return rows;
}

export function unreadNotificationCount(rows: AppNotification[]) {
  return rows.filter((row) => row.status === 'unread').length;
}

export function canDismissNotification(row: Pick<AppNotification, 'priority' | 'dismissible'>) {
  return row.priority !== 'critical' && row.dismissible !== false;
}

export function buildNotifications(source: ReportSourceData, now = todayISO(), states: NotificationState[] = []) {
  const currentMonth = monthKey(now);
  const previousMonth = previousMonthKey(now);
  const alerts: AppNotification[] = [];
  const activeTransactions = source.transactions.filter((row) => activeTransaction(row.status, row.active));
  const activePlans = source.plans.filter((plan) => activePlan(String(plan.status || 'active'), plan.active));
  const accounts = source.accounts.filter((account) => account.status !== 'deleted' && account.active !== false);
  const liquidAccounts = accounts.filter((account) => account.type !== 'credit_card');
  const cardAccounts = accounts.filter((account) => account.type === 'credit_card');

  activePlans.forEach((plan) => {
    const dueDate = normalizeDateISO(plan.nextDueDate || plan.dueDate);
    const delta = daysBetween(now, dueDate);
    if (delta < 0) {
      alerts.push(baseNotification({
        key: `plan:${plan.id}:${dueDate}:overdue`,
        type: 'payment',
        category: 'payment',
        source: 'plans',
        priority: 'high',
        title: 'Payment overdue',
        message: `${plan.title} was due ${displayDate(dueDate)} for ${formatMoney(plan.amount)}.`,
        relatedType: 'plan',
        relatedId: plan.id,
        entityId: plan.id,
        dueDate,
        action: action('Open Plan', 'upcoming')
      }));
    } else if (delta === 0) {
      alerts.push(baseNotification({
        key: `plan:${plan.id}:${dueDate}:due_today`,
        type: 'payment',
        category: 'payment',
        source: 'plans',
        priority: 'high',
        title: 'Payment due today',
        message: `${plan.title} is due today for ${formatMoney(plan.amount)}.`,
        relatedType: 'plan',
        relatedId: plan.id,
        entityId: plan.id,
        dueDate,
        action: action('Open Plan', 'upcoming')
      }));
    } else if (delta <= 3) {
      alerts.push(baseNotification({
        key: `plan:${plan.id}:${dueDate}:due_3d`,
        type: 'payment',
        category: 'payment',
        source: 'plans',
        priority: 'normal',
        title: 'Payment due soon',
        message: `${plan.title} is due in ${delta} day(s) for ${formatMoney(plan.amount)}.`,
        relatedType: 'plan',
        relatedId: plan.id,
        entityId: plan.id,
        dueDate,
        action: action('Open Plan', 'upcoming')
      }));
    }
    if (!plan.paidFromAccountId) {
      alerts.push(baseNotification({
        key: `plan:${plan.id}:missing_account`,
        type: 'payment',
        category: 'payment',
        source: 'plans',
        priority: 'low',
        title: 'Plan missing account',
        message: `${plan.title} has no paid-from account, so account deduction will be manual.`,
        relatedType: 'plan',
        relatedId: plan.id,
        entityId: plan.id,
        dueDate,
        action: action('Update Plan', 'upcoming')
      }));
    }
  });

  const currentBudgets = source.budgets.filter((budget) => activeBudget(budget.status, budget.active) && budget.monthKey === currentMonth);
  buildBudgetUsage(currentBudgets, activeTransactions.filter((row) => row.monthKey === currentMonth)).forEach((budget) => {
    if (budget.usagePct < 80) return;
    const categoryKey = cleanKey(budget.category);
    const crossed = budget.usagePct >= 100;
    alerts.push(baseNotification({
      key: `budget:${budget.monthKey}:${categoryKey}:${crossed ? '100' : '80'}`,
      type: 'budget',
      category: 'budget',
      source: 'budgets',
      priority: crossed ? 'high' : 'normal',
      title: crossed ? 'Budget exceeded' : 'Budget above 80%',
      message: `${budget.category} is ${budget.usagePct}% used. Spent ${formatMoney(budget.spent)} of ${formatMoney(budget.limitAmount)}.`,
      relatedType: 'budget',
      relatedId: budget.id,
      entityId: budget.id,
      dateISO: `${budget.monthKey}-01`,
      action: action('Open Budgets', 'setup')
    }));
  });

  liquidAccounts.forEach((account) => {
    if (Number(account.currentBalance || 0) < 0) {
      alerts.push(baseNotification({
        key: `account:${account.id}:negative_balance`,
        type: 'account',
        category: 'account',
        source: 'accounts',
        priority: 'high',
        title: 'Negative account balance',
        message: `${account.name} balance is ${formatMoney(account.currentBalance)}.`,
        relatedType: 'account',
        relatedId: account.id,
        entityId: account.id,
        action: action('Open Account', 'setup')
      }));
    }
  });

  cardAccounts.forEach((card) => {
    const limit = Number(card.creditLimit || 0);
    const outstanding = Math.max(Number(card.currentBalance || 0), 0);
    if (limit <= 0) return;
    const utilization = Math.round((outstanding / limit) * 100);
    if (utilization >= 100) {
      alerts.push(baseNotification({
        key: `card:${card.id}:${currentMonth}:utilization100`,
        type: 'credit_card',
        category: 'credit_card',
        source: 'accounts',
        priority: 'critical',
        title: 'Credit card over limit',
        message: `${card.name} utilization is ${utilization}% of ${formatMoney(limit)}.`,
        relatedType: 'account',
        relatedId: card.id,
        entityId: card.id,
        action: action('Open Card', 'setup'),
        dismissible: false
      }));
    } else if (utilization >= 80) {
      alerts.push(baseNotification({
        key: `card:${card.id}:${currentMonth}:utilization80`,
        type: 'credit_card',
        category: 'credit_card',
        source: 'accounts',
        priority: 'normal',
        title: 'High credit-card utilization',
        message: `${card.name} utilization is ${utilization}%. Outstanding ${formatMoney(outstanding)}.`,
        relatedType: 'account',
        relatedId: card.id,
        entityId: card.id,
        action: action('Open Card', 'setup')
      }));
    }
  });

  const auditMonths = new Set([currentMonth, previousMonth]);
  source.balanceAudits
    .filter((audit) => auditMonths.has(audit.monthKey) && (Number(audit.difference || 0) !== 0 || Number(audit.availableLimitDifference || 0) !== 0))
    .forEach((audit) => {
      alerts.push(baseNotification({
        key: `audit:${audit.accountId}:${audit.monthKey}:mismatch`,
        type: 'reconciliation',
        category: 'reconciliation',
        source: 'balanceAudits',
        priority: 'high',
        title: 'Reconciliation mismatch',
        message: `${audit.accountName} has an audit difference for ${audit.monthKey}.`,
        relatedType: 'account',
        relatedId: audit.accountId,
        entityId: audit.accountId,
        dateISO: audit.dateISO,
        action: action('Open Monthly Close', 'monthlyClose')
      }));
    });

  const periodMap = new Map((source.monthlyPeriods || []).map((period) => [period.monthKey, period]));
  const previousPeriod = periodMap.get(previousMonth);
  if (hasMonthActivity(source, previousMonth) && (!previousPeriod || previousPeriod.status === 'OPEN')) {
    alerts.push(baseNotification({
      key: `period:${previousMonth}:review_pending`,
      type: 'monthly_close',
      category: 'monthly_close',
      source: 'monthlyPeriods',
      priority: 'normal',
      title: 'Previous month review pending',
      message: `${previousMonth} has activity and is not closed yet.`,
      relatedType: 'monthlyPeriod',
      relatedId: previousMonth,
      entityId: previousMonth,
      dateISO: `${previousMonth}-01`,
      action: action('Open Monthly Close', 'monthlyClose')
    }));
  }
  (source.monthlyPeriods || []).filter((period) => period.status === 'REOPENED').forEach((period) => {
    alerts.push(baseNotification({
      key: `period:${period.monthKey}:reopened`,
      type: 'monthly_close',
      category: 'monthly_close',
      source: 'monthlyPeriods',
      priority: 'high',
      title: 'Month reopened',
      message: `${period.monthKey} is open for corrections and should be reclosed after review.`,
      relatedType: 'monthlyPeriod',
      relatedId: period.monthKey,
      entityId: period.monthKey,
      dateISO: `${period.monthKey}-01`,
      action: action('Open Monthly Close', 'monthlyClose')
    }));
  });

  buildFinancialHealthModel(source, now).issues
    .map(healthIssueToNotification)
    .filter(Boolean)
    .forEach((alert) => alerts.push(alert as AppNotification));

  const storedEvents = states.map(storedEventNotification).filter(Boolean) as AppNotification[];
  const merged = mergeState(alerts.concat(storedEvents), states);
  return visibleNotifications(merged);
}
